import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldAlert, Eye, EyeOff, Loader2, Check, Mail, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Blocking modal that fires when the authenticated user's account has
 * `must_change_password: true` (set by an admin password reset). Until the
 * user picks a fresh password the client stays locked on this screen so
 * iCloud Keychain / Chrome Autofill can't repopulate the stale temp
 * credential on their next visit.
 *
 * Mounted exclusively from `SubscriptionLayer` so it benefits from the
 * same global AuthContext + status polling pattern as NicknameOnboarding.
 */
export default function ForcePasswordChange({ onCompleted }) {
  const { checkAuth, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const strongEnough = pw.length >= 8;
  const matches = pw && pw === confirm;
  const canSubmit = strongEnough && matches && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.post('/auth/complete-forced-change', { new_password: pw });
      toast.success('Password updated — please sign in with your new password');
      // Server revoked every session, so re-auth is mandatory. We log the
      // user out and let the normal login flow take over; iCloud Keychain
      // will prompt to save the fresh password on the next successful login.
      try { await logout(); } catch { /* best-effort */ }
      onCompleted?.();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not update password. Please try again.';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="force-password-change"
      role="dialog"
      aria-modal="true"
    >
      <motion.form
        onSubmit={submit}
        initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 24, stiffness: 300 }}
        className="w-full max-w-sm rounded-3xl bg-neutral-950 border border-amber-400/40 p-6 shadow-2xl shadow-amber-500/10"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Mandatory</p>
            <h2 className="font-serif text-xl leading-tight">Set a new password</h2>
            <p className="text-xs text-muted-foreground mt-1">
              An administrator reset your WildQuest password. Pick a new one now so
              you can sign in from any device without using the temporary one.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div className="relative">
            <label className="sr-only" htmlFor="fpc-new">New password</label>
            <input
              id="fpc-new"
              type={showPw ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="New password (8+ characters)"
              autoFocus
              autoComplete="new-password"
              data-testid="fpc-new-password"
              className="w-full px-3 py-2.5 pr-9 rounded-lg bg-white/5 border border-white/15 text-sm focus:outline-none focus:border-accent/60"
            />
            <button
              type="button"
              onClick={() => setShowPw(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-white"
              aria-label={showPw ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div>
            <label className="sr-only" htmlFor="fpc-confirm">Confirm new password</label>
            <input
              id="fpc-confirm"
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
              data-testid="fpc-confirm-password"
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-sm focus:outline-none focus:border-accent/60"
            />
          </div>
          <ul className="text-[11px] space-y-1 text-muted-foreground">
            <li className={strongEnough ? 'text-emerald-300' : ''}>
              {strongEnough ? '✓' : '•'} At least 8 characters
            </li>
            <li className={matches ? 'text-emerald-300' : ''}>
              {matches ? '✓' : '•'} Passwords match
            </li>
          </ul>
          {err && (
            <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2" data-testid="fpc-error">
              {err}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="fpc-submit"
          className="mt-5 w-full py-2.5 rounded-full bg-accent text-black font-semibold uppercase text-xs tracking-widest disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Update password
        </button>
        <p className="text-center text-[10px] uppercase tracking-widest text-muted-foreground mt-3">
          You'll sign in again with the new password
        </p>

        {/* Escape hatches — a stuck user must never be permanently trapped
            here. "Email me a reset link" opens the public forgot-password
            flow; "Sign out" lets them switch accounts entirely. */}
        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={async () => {
              try { await logout(); } catch { /* best-effort */ }
              nav('/forgot-password');
            }}
            data-testid="fpc-forgot"
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-accent hover:text-white transition"
          >
            <Mail className="w-3 h-3" /> Email me a reset link
          </button>
          <button
            type="button"
            onClick={async () => {
              try { await logout(); } catch { /* best-effort */ }
              nav('/');
            }}
            data-testid="fpc-signout"
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-white transition"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
